import { CursorArrowRaysIcon, IconButton } from 'components'
import { useEffect, useState } from 'react'
import { Action, ILayout } from 'types'
import { LayoutHelpers } from '../../desktopTypes'
import { isDesktop } from '../../utils/helpers'
import {
  getDefaultActionDimensions,
  keyFromPath,
  parsePadding,
  useLayout,
  useParseText,
} from '../../utils/layoutUtils'
import { useScale } from '../../utils/scaleUtils'
import { hideTool, initTool, showTool } from '../../utils/toolUtils'
import { useExplorerConfig, useLayoutContext } from '../providers'
import { SDialog, SDivider, SIcon, SKitIcon, SText, SView, SViewProps } from '../scaled'
import { HeroIcon } from '../scaled/SIcon'
import {
  CarouselLayout,
  FlexLayout,
  GreetingLayout,
  GridLayout,
  MediaLayout,
  SelectedTraitLayout,
  TabsLayout,
  TextLayout,
  TraitLayout,
} from './core'

type Props = Partial<ILayout> & LayoutHelpers
type LayoutWithHelpers = ILayout & LayoutHelpers

export default function Layout(props: Props) {
  // TODO - handle requiredTrait

  const key = keyFromPath(props.keyPath)
  const layout = useLayout(key)
  const style = { ...layout?.style, ...props?.style }
  const mergedLayout = { ...layout, ...props, style } as LayoutWithHelpers

  return mergedLayout.action ? (
    <ActionLayout {...mergedLayout} />
  ) : (
    <DisplayLayout {...mergedLayout} />
  )
}

function ActionLayout({ action, ...rest }: LayoutWithHelpers) {
  const scale = useScale()
  const { templateDocs } = useExplorerConfig()
  const { onClick } = useLayoutContext()
  const parseText = useParseText()
  const [showAction, setShowAction] = useState(false)
  const [toolKey, setToolKey] = useState<string>()

  const actionLayout = templateDocs?.template.layoutMap?.[action!.layoutKey]
  const isTool = actionLayout?.type === 'tool'
  const isMedia = actionLayout?.type === 'media'
  const tool = isTool ? templateDocs?.tools.find(t => t.id === actionLayout.toolId) : undefined
  const { height, width } = getDefaultActionDimensions(action, templateDocs)

  function openAction() {
    if (tool) {
      if (isDesktop) showTool(toolKey)
      else window.open(tool.url, '_blank')?.focus()
    } else {
      setShowAction(true)
    }
  }

  function closeAction() {
    setShowAction(false)
    hideTool(toolKey)
  }

  useEffect(() => {
    if (!isDesktop || !tool) return

    const maxWidthPx = 1200 * scale
    const maxHeightPx = 700 * scale
    initTool(tool, maxWidthPx, maxHeightPx).then(setToolKey)

    return () => {
      hideTool(toolKey)
    }
  }, [])

  return (
    <LayoutContainer onClick={openAction} {...rest}>
      <SView position="static" style={{ flex: 1 }}>
        <LayoutContent {...rest} />
        <ActionIcon icon={action?.icon} />
        {onClick && (
          <IconButton
            size="xs"
            variant="solid"
            colorPalette="secondary"
            onClick={() => {
              openAction()
              onClick(rest.keyPath)
            }}
            position="absolute"
            right={-1.5}
            bottom={-0.5}
            borderRadius={0}
            borderTopLeftRadius="full"
            aria-label="Open action"
            icon={<CursorArrowRaysIcon size="sm" />}
          />
        )}
      </SView>

      {action?.type === 'dialog' && !tool && (
        <SDialog
          modal={false}
          contentProps={{
            backgroundColor: isMedia ? 'black' : '#00000077',
            onClick: e => e.stopPropagation(),
          }}
          onClose={closeAction}
          open={showAction}
          title={parseText(action?.title)}
          closeOnInteractOutside={!onClick}
        >
          <Layout
            playVideo={showAction}
            style={{ borderRadius: 0, height, width, ...action.layoutStyle }}
            keyPath={`${rest.keyPath}/${action.layoutKey}`}
          />
        </SDialog>
      )}
    </LayoutContainer>
  )
}

function DisplayLayout(props: LayoutWithHelpers) {
  return (
    <LayoutContainer {...props}>
      <LayoutContent {...props} />
    </LayoutContainer>
  )
}

function LayoutContainer(props: LayoutWithHelpers & { children: any; onClick?: () => void }) {
  const { children, style, type, keyPath, onClick: _onClick } = props
  const { background, basis, height, width, padding, grow, shrink, borderRadius, borderWidth } =
    style || {}
  const { templateDocs } = useExplorerConfig()
  const { onClick, selectedKey } = useLayoutContext()
  const backgroundUrl = templateDocs?.media.find(m => m.id === background?.mediaId)?.url
  const key = keyFromPath(props.keyPath)
  const handleClick = !!key ? onClick || _onClick : _onClick
  const isSelected = key && selectedKey === key
  const outerStyle: SViewProps = {
    h: height,
    w: width,
    maxH: '100%',
    maxW: '100%',
    grow,
    basis,
    shrink: shrink ?? (basis || type === 'media' ? 0 : 1),
    backgroundColor: background?.color,
    borderColor: isSelected ? 'cyan' : '#85858566',
    borderRadius,
    overflow: 'hidden',
    borderWidth: isSelected ? 2 : borderWidth,
    cursor: handleClick ? 'pointer' : undefined,
    p: 0,
    onClick: handleClick
      ? e => {
          e.stopPropagation()
          handleClick(keyPath)
        }
      : undefined,
  }
  const innerStyle: SViewProps = {
    ...(background?.blur && { backdropFilter: 'blur(40px)' }),
  }

  if (backgroundUrl) {
    return (
      <SView
        backgroundImage={`url(${backgroundUrl})`}
        backgroundSize="cover"
        backgroundPosition="center"
        {...outerStyle}
      >
        <SView
          {...innerStyle}
          grow={grow}
          backgroundImage="linear-gradient(rgba(0, 0, 0, 0.49), rgba(0, 0, 0, 0.90))"
        >
          {children}
        </SView>
      </SView>
    )
  }

  return (
    <SView {...outerStyle} {...innerStyle}>
      {children}
    </SView>
  )
}

function LayoutContent(props: LayoutWithHelpers) {
  const parseText = useParseText()
  const { theme } = useExplorerConfig()

  const { text, icon, iconFill } = props.title || {}
  if (!text) return <LayoutSwitch {...props} />

  return (
    <SView overflow="hidden" grow={1} gap={8}>
      <SView {...parsePadding(props, true)} gap={8}>
        <SView direction="row" gap={8} alignItems="center">
          {icon ? (
            iconFill ? (
              <SView
                w={24}
                h={24}
                backgroundColor={iconFill === 'primary' ? theme.color.primary : iconFill}
                align="center"
                justify="center"
              >
                <SKitIcon fontSize={18} type={icon} color="black" />
              </SView>
            ) : (
              <SKitIcon fontSize={24} type={icon} />
            )
          ) : null}

          <SText fontSize={20} fontWeight="bold">
            {parseText(text)}
          </SText>
        </SView>

        <SDivider />
      </SView>

      <LayoutSwitch {...props} />
    </SView>
  )
}

function ActionIcon({ icon }: { icon?: Action['icon'] }) {
  if (!icon) return null

  const value = ((): HeroIcon | undefined => {
    switch (icon.type) {
      case 'arrow':
        return 'arrow-up-right'
      case 'document':
        return 'document'
      case 'globe':
        return 'globe-alt'
      case 'more':
        return 'ellipsis-horizontal-circle'
      case 'play':
        return 'play-circle'
      case 'plus':
        return 'plus-circle'
      case 'qr':
        return 'qr-code'
    }
  })()

  if (!value) return null

  return <SIcon value={value} type="outline" position="absolute" right={8} top={8} />
}

function LayoutSwitch(props: LayoutWithHelpers) {
  switch (props.type) {
    case 'carousel':
      return <CarouselLayout {...props} />
    case 'flex':
      return <FlexLayout {...props} />
    case 'greeting':
      return <GreetingLayout {...props} />
    case 'grid':
      return <GridLayout {...props} />
    case 'media':
      return <MediaLayout {...props} />
    case 'selected-traits':
      return <SelectedTraitLayout {...props} />
    case 'tabs':
      return <TabsLayout {...props} />
    case 'text':
      return <TextLayout {...props} />
    case 'trait':
      return <TraitLayout {...props} />
  }
}
